import {useRef, useState} from 'react';

import {FilterFieldName} from '../components/FilterEditor/utils/getFilterSections';
import {useFilters} from '../components/FiltersContext/FiltersContext';
import {FiltersType} from '../types/FiltersType';
import {createAuctionFilterBody} from '../utils/createAuctionFilterBody';
import {createFilterBody} from '../utils/createFilterBody';
import {useFilterDraft} from './useFilterDraft';
import {useSourcingFilterPresets} from './useSourcingFilterPresets';

type ListingSnapshot = {
  classifieds: ReturnType<typeof createFilterBody> | null;
  auctions: ReturnType<typeof createAuctionFilterBody> | null;
  count: number | undefined;
};

export function useFilterEditingSession(options: {
  draft: ReturnType<typeof useFilterDraft>;
  getListingSnapshot: () => ListingSnapshot;
}) {
  const {filters, onChange} = useFilters();
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [editingField, setEditingField] = useState<FilterFieldName | null>(null);
  const chipSnapshotRef = useRef<FiltersType | null>(null);
  const restorePresetRef = useRef<(() => void) | undefined>(undefined);
  const isEditing = isFiltersOpen || editingField !== null;
  const filterPresetsControl = useSourcingFilterPresets(isEditing);
  const listingSnapshotRef = useRef<ListingSnapshot | null>(null);
  const beginEditing = () => {
    if (!isEditing) {
      listingSnapshotRef.current = {
        ...options.getListingSnapshot(),
      };
      restorePresetRef.current = filterPresetsControl.createEditingCheckpoint?.();
      options.draft.begin();
    }
  };
  const openFiltersView = () => {
    beginEditing();
    setFiltersOpen(true);
  };
  const cancelEditing = () => {
    restorePresetRef.current?.();
    options.draft.cancel();
    setFiltersOpen(false);
    setEditingField(null);
  };
  const applyEditing = () => {
    options.draft.apply();
    setFiltersOpen(false);
    setEditingField(null);
  };
  const editChip = (field: FilterFieldName) => {
    chipSnapshotRef.current = filters;
    beginEditing();
    setEditingField(field);
  };
  const continueEditing = () => {
    setEditingField(null);
  };
  const cancelChip = () => {
    if (!isFiltersOpen) {
      cancelEditing();
      return;
    }
    if (chipSnapshotRef.current) {
      onChange(chipSnapshotRef.current);
    }
    setEditingField(null);
  };

  return {
    isFiltersOpen,
    editingField,
    isEditing,
    filterPresetsControl,
    listingSnapshot: listingSnapshotRef.current,
    openFiltersView,
    applyEditing,
    continueEditing,
    editChip,
    cancelChip,
  };
}
